import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import { format } from 'date-fns';

interface JapaneseDatePickerProps {
  label: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'filled' | 'standard';
  margin?: 'none' | 'dense' | 'normal';
}

const JapaneseDatePicker: React.FC<JapaneseDatePickerProps> = ({
  label,
  value,
  onChange,
  error = false,
  helperText,
  disabled = false,
  placeholder,
  fullWidth = true,
  size = 'medium',
  variant = 'outlined',
  margin = 'none'
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentMonth, setCurrentMonth] = useState(
    value ? new Date(value.getFullYear(), value.getMonth(), 1) : new Date()
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // 令和年を計算する関数
  const getRewaYear = (year: number): number => {
    return year - 2018; // 2019年が令和元年
  };

  // 月の日数を取得
  const getDaysInMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  // 月の最初の日の曜日を取得
  const getFirstDayOfMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  // 今日の日付
  const today = new Date();
  const isToday = (date: Date): boolean => {
    return date.toDateString() === today.toDateString();
  };

  // 日付が選択されているかチェック
  const isSelected = (date: Date): boolean => {
    return value ? date.toDateString() === value.toDateString() : false;
  };

  // 前月に移動
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  // 次月に移動
  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // 日付を選択
  const selectDate = (day: number) => {
    const selectedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    onChange(selectedDate);
    handleClose();
  };

  // 削除
  const clearDate = () => {
    onChange(null);
    handleClose();
  };

  // 今日を選択
  const selectToday = () => {
    const todayDate = new Date(today);
    onChange(todayDate);
    setCurrentMonth(new Date(todayDate.getFullYear(), todayDate.getMonth(), 1));
    handleClose();
  };

  // カレンダーを描画
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];

    // 空のセル（前月の末尾）
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <Box key={`empty-${i}`} sx={{ width: 32, height: 32 }} />
      );
    }

    // 日付のセル
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const dayOfWeek = date.getDay();
      const isTodayDate = isToday(date);
      const isSelectedDate = isSelected(date);

      let textColor = '#000000';
      if (dayOfWeek === 0) textColor = '#f44336'; // 日曜日（赤）
      if (dayOfWeek === 6) textColor = '#2196f3'; // 土曜日（青）

      days.push(
        <Box
          key={day}
          sx={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: textColor,
            backgroundColor: isSelectedDate ? '#2196f3' : isTodayDate ? '#e0e0e0' : 'transparent',
            borderRadius: '4px',
            ...(isSelectedDate && { color: '#ffffff' }),
            '&:hover': {
              backgroundColor: isSelectedDate ? '#1976d2' : '#e3f2fd',
              color: isSelectedDate ? '#ffffff' : '#1976d2',
            },
          }}
          onClick={() => selectDate(day)}
        >
          {day}
        </Box>
      );
    }

    return days;
  };

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth() + 1;
  const rewaYear = getRewaYear(year);

  return (
    <>
      <TextField
        label={label}
        value={value ? format(value, 'yyyy/MM/dd') : ''}
        onClick={handleClick}
        placeholder={placeholder}
        InputProps={{
          readOnly: true,
        }}
        fullWidth={fullWidth}
        size={size}
        variant={variant}
        margin={margin}
        error={error}
        helperText={helperText}
        disabled={disabled}
        sx={{ 
          cursor: disabled ? 'default' : 'pointer',
          '& .MuiInputBase-input': {
            cursor: disabled ? 'default' : 'pointer',
          }
        }}
      />
      
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              mt: 1,
              border: '1px solid #e0e0e0',
            }
          }
        }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          {/* カレンダーヘッダー */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
              {year}年（令和{rewaYear}年）{month}月
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={goToPreviousMonth}
                sx={{ 
                  p: 0.5,
                  minWidth: 32,
                  minHeight: 32,
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                }}
              >
                <Typography sx={{ fontSize: '1.1rem', color: '#666', fontWeight: 'bold', userSelect: 'none' }}>
                  ←
                </Typography>
              </IconButton>
              <IconButton
                size="small"
                onClick={goToNextMonth}
                sx={{ 
                  p: 0.5,
                  minWidth: 32,
                  minHeight: 32,
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                }}
              >
                <Typography sx={{ fontSize: '1.1rem', color: '#666', fontWeight: 'bold', userSelect: 'none' }}>
                  →
                </Typography>
              </IconButton>
            </Box>
          </Box>

          {/* 曜日ヘッダー */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
            {['日', '月', '火', '水', '木', '金', '土'].map((day, index) => (
              <Box
                key={day}
                sx={{
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  py: 0.5,
                  color: index === 0 ? '#f44336' : index === 6 ? '#2196f3' : '#333',
                  userSelect: 'none',
                }}
              >
                {day}
              </Box>
            ))}
          </Box>

          {/* カレンダーグリッド */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 2 }}>
            {renderCalendar()}
          </Box>

          {/* フッターボタン */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #e0e0e0' }}>
            <Button
              size="small"
              onClick={clearDate}
              sx={{ 
                color: '#666',
                fontSize: '0.875rem',
                minWidth: 'auto',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
              }}
            >
              削除
            </Button>
            <Button
              size="small"
              onClick={selectToday}
              sx={{ 
                color: '#2196f3',
                fontSize: '0.875rem',
                fontWeight: 600,
                minWidth: 'auto',
                '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.04)' }
              }}
            >
              今日
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default JapaneseDatePicker;